import { Button, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import ModalV2 from '/@/components/bayer/modal-v2';
import { useMyFields } from '/@/hooks/fields';
import { useOwnUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { getSessionCountry } from '/@/store/session/selectors';
import type { VisitReportEntrySource } from '/@/store/tracking/events/visit-report';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Field } from '/@/types/field';
import { reverse } from '/@/utils/router';
import NewFieldGrowersModal from '/@/views/dashboard/growers/components/new-field-modal';
import type { FieldRegistryFlow } from '/@/views/fields/field-monitoring/components/detail/registries/components/add/flow-tracking';
import {
    BEACON,
    flowNavigationState,
    registryVisit,
    reportEventProps,
    startVisitReportFlow,
    trackRegistryVisitAbandoned,
    type VisitReportFlow,
} from '/@/views/visit-reports/flow-tracking';
import FieldSelectorModal from '../../../../../../views/dashboard/growers/components/field-selector-modal';

const CreateVisitModal = ({
    open,
    setOpen,
    field,
    userNodeId,
    entrySource,
    registryFlow,
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
    field?: Field;
    userNodeId: number;
    /** Where the report begins, for its `flow:Started`. */
    entrySource: VisitReportEntrySource;
    /** The attempt the report belongs to, when it is the visit of "Agregar registro". */
    registryFlow?: FieldRegistryFlow;
}) => {
    const [openCreateFieldModal, setOpenCreateFieldModal] = useState(false);
    const country = useSelector(getSessionCountry);
    const ownUserNode = useOwnUserNode();
    const location = useLocation();

    // Construct return URL with openVisitReport param for field creation flow
    const getReturnUrl = () => {
        const params = new URLSearchParams(location.search);
        params.set('openVisitReport', 'true');
        return `${location.pathname}?${params.toString()}`;
    };
    const allFieldsQuery = useMyFields({
        page: 1,
        pageSize: 1,
        country: country.code,
        selectOnly: 'id',
        userNodeId,
        includeChildren: false,
    });
    const navigate = useNavigate();

    // Opening the field selection starts the report's attempt. A ref rather than state: the
    // StrictMode remount in development must not start a second one.
    const flow = useRef<VisitReportFlow | undefined>(undefined);
    // biome-ignore lint/correctness/useExhaustiveDependencies: started once per open, with the entry it was opened from
    useEffect(() => {
        if (open && !flow.current)
            flow.current = startVisitReportFlow(entrySource, registryFlow ? registryVisit(registryFlow) : undefined);
    }, [open]);

    // Leaving the selection without starting the report ends the attempt there.
    const close = () => {
        if (flow.current)
            mpTrackEvent(
                'Visit report flow:Ended',
                {
                    ...reportEventProps({ flowId: flow.current.flowId }),
                    last_step: 'field_selection',
                    elements_count: 0,
                    had_draft: false,
                },
                BEACON
            );
        if (flow.current?.registry)
            trackRegistryVisitAbandoned(flow.current.registry, 'field_selection', 'close', false);
        flow.current = undefined;
        setOpen(false);
    };

    const isOwnUserNode = ownUserNode.data?.id === userNodeId;

    if (allFieldsQuery.isValidating) return null;

    if (!allFieldsQuery.data?.count) {
        return (
            <>
                <ModalV2
                    open={open}
                    onClose={close}
                    title={_('Agregá un lote para empezar')}
                    actions={
                        <Stack direction='row' spacing={1} justifyContent='flex-end' sx={{ width: '100%' }}>
                            <Button variant='text' color='secondary' onClick={close}>
                                {_('Cancelar')}
                            </Button>
                            <Button variant='contained' color='secondary' onClick={() => setOpenCreateFieldModal(true)}>
                                {_('Agregar lote')}
                            </Button>
                        </Stack>
                    }
                >
                    <Stack spacing={2} width='100%'>
                        <Typography variant='subtitle1' color='primary' textAlign='center'>
                            {isOwnUserNode
                                ? _(
                                      'Para registrar observaciones y generar el informe de visita, primero necesitás agregar un lote. Una vez creado, vas a poder cargar los datos de la visita.'
                                  )
                                : _(
                                      'Para registrar observaciones y generar el informe de visita, primero necesitás agregar un lote de tu productor. Una vez creado, vas a poder cargar los datos de la visita.'
                                  )}
                        </Typography>
                    </Stack>
                </ModalV2>
                <NewFieldGrowersModal
                    open={openCreateFieldModal}
                    setOpen={setOpenCreateFieldModal}
                    growerUserNodeId={userNodeId}
                    trackEntrySource={'visit-report'}
                    returnUrl={getReturnUrl()}
                />
            </>
        );
    }

    return (
        <FieldSelectorModal
            ModalV2Props={{
                open: open,
                onClose: close,
                // A report can be a visit or remote (without visiting the field)
                title: _('Seleccioná los lotes para el informe'),
            }}
            text={_(
                'Vas a registrar observaciones, notas, subir fotos e incluir gráficos o datos relevantes de cada visita al lote'
            )}
            userNodeId={userNodeId}
            searchPlaceholder={_('Buscá los lotes por nombre')}
            action={{
                onClick: (fieldIds: number[]) => {
                    const started = flow.current;
                    if (started)
                        mpTrackEvent('Visit report field selected', {
                            ...reportEventProps({ flowId: started.flowId }),
                            fields_count: fieldIds.length,
                            entry_source: entrySource,
                        });
                    navigate(
                        reverse('visitReport.VisitReportEdit', {
                            qs: {
                                fieldIds: fieldIds.join(','),
                            },
                        }),
                        // The attempt goes on in the editor
                        started ? { state: flowNavigationState(started) } : undefined
                    );
                },
                text: _('Empezar informe'),
            }}
            field={field}
        />
    );
};

export default CreateVisitModal;
